if (!customElements.get('k-cart')) {
  customElements.define(
    'k-cart',
    class KCart extends HTMLElement {
      constructor() {
        super();
        this.toastTimer = null;
        this.activeElement = null;
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleOutsidePointer = this.handleOutsidePointer.bind(this);
        this.handleResize = this.positionToast.bind(this);
      }

      connectedCallback() {
        this.trigger = document.querySelector('[data-k-cart-trigger]');
        this.toast = this.querySelector('#KCartToast');
        this.drawer = this.querySelector('#KCartDrawer');

        if (this.trigger) {
          this.trigger.setAttribute('aria-haspopup', 'dialog');
          this.trigger.setAttribute('aria-expanded', 'false');
          this.trigger.addEventListener('click', this.handleTriggerClick);
        }

        this.addEventListener('click', this.handleClick);
        document.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('pointerdown', this.handleOutsidePointer, true);
        window.addEventListener('resize', this.handleResize);
        this.syncTriggerLabel();
      }

      disconnectedCallback() {
        if (this.trigger) this.trigger.removeEventListener('click', this.handleTriggerClick);
        this.removeEventListener('click', this.handleClick);
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('pointerdown', this.handleOutsidePointer, true);
        window.removeEventListener('resize', this.handleResize);
        window.clearTimeout(this.toastTimer);
      }

      handleTriggerClick(event) {
        event.preventDefault();
        this.openDrawer(this.trigger);
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          if (this.classList.contains('is-open')) this.closeDrawer();
          if (this.classList.contains('is-toast-open')) this.hideToast();
          return;
        }

        if (event.key !== 'Tab' || !this.classList.contains('is-open') || !this.drawer) return;

        const focusable = Array.from(
          this.drawer.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => !element.hasAttribute('hidden') && element.getClientRects().length > 0);

        if (!focusable.length) {
          event.preventDefault();
          this.drawer.focus({ preventScroll: true });
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && (active === first || !this.drawer.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }

      handleOutsidePointer(event) {
        if (!this.classList.contains('is-open') || !this.drawer) return;
        const target = event.target;
        if (!(target instanceof Node)) return;
        const trigger = document.querySelector('[data-k-cart-trigger]');
        if (this.drawer.contains(target)) return;
        if (trigger && trigger.contains(target)) return;
        this.closeDrawer(false);
      }

      handleClick(event) {
        const openButton = event.target.closest('[data-k-cart-open]');
        if (openButton) {
          event.preventDefault();
          this.openDrawer(openButton);
          return;
        }

        if (event.target.closest('[data-k-cart-close]')) {
          event.preventDefault();
          this.closeDrawer();
          return;
        }

        if (event.target.closest('[data-k-toast-close]')) {
          event.preventDefault();
          this.hideToast();
          return;
        }

        const actionButton = event.target.closest('[data-k-cart-action]');
        if (!actionButton) return;

        const line = actionButton.closest('.k-cart-line');
        if (!line || this.classList.contains('is-updating')) return;

        const action = actionButton.dataset.kCartAction;
        const lineIndex = Number(line.dataset.line || 0);
        const currentQty = Number(line.dataset.quantity || 1);
        if (!lineIndex) return;

        let nextQty = currentQty;
        if (action === 'increment') nextQty = currentQty + 1;
        if (action === 'decrement') nextQty = Math.max(0, currentQty - 1);
        if (action === 'remove') nextQty = 0;

        this.changeLine(lineIndex, nextQty);
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      getSectionsToRender() {
        return [
          { id: 'cart-notification-product' },
          { id: 'cart-drawer' },
          { id: 'cart-icon-bubble' },
        ];
      }

      renderContents(parsedState) {
        this.cartItemKey = parsedState.key;
        const sections = parsedState.sections || {};

        if (sections['cart-drawer']) this.replaceDrawer(sections['cart-drawer']);
        if (sections['cart-icon-bubble']) this.replaceIcon(sections['cart-icon-bubble']);
        if (sections['cart-notification-product']) {
          const fragment = this.getSectionNode(
            sections['cart-notification-product'],
            '#cart-notification-product-' + CSS.escape(String(this.cartItemKey))
          );
          const target = this.querySelector('#KCartToastProduct');
          if (fragment && target) target.innerHTML = fragment.innerHTML;
        }

        this.syncTriggerLabel();
        this.showToast();
      }

      getSectionNode(html, selector) {
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }

      replaceDrawer(html) {
        const fresh = this.getSectionNode(html, '#KCartDrawerInner');
        const current = this.querySelector('#KCartDrawerInner');
        if (fresh && current) current.replaceWith(fresh);
        if (this.classList.contains('is-open')) this.loadDeferredImages();
      }

      loadDeferredImages() {
        this.querySelectorAll('img[data-k-src]').forEach((img) => {
          img.src = img.getAttribute('data-k-src');
          img.removeAttribute('data-k-src');
        });
      }

      replaceIcon(html) {
        const documentFromSection = new DOMParser().parseFromString(html, 'text/html');
        const wrapper = documentFromSection.querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (wrapper && target) target.innerHTML = wrapper.innerHTML;
        this.trigger = document.querySelector('[data-k-cart-trigger]');
      }

      syncTriggerLabel() {
        const target = document.querySelector('#cart-icon-bubble');
        if (!target) return;
        const countNode = target.querySelector('[data-k-cart-count]');
        const count = countNode ? Number(countNode.textContent.trim()) || 0 : 0;
        target.setAttribute('aria-label', 'Panier, ' + count + ' référence' + (count === 1 ? '' : 's'));
      }

      positionToast() {
        if (!this.toast) return;
        const trigger = document.querySelector('[data-k-cart-trigger]');
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const right = Math.max(12, window.innerWidth - rect.right);
        const top = Math.max(12, rect.bottom + 10);
        this.toast.style.setProperty('--k-cart-toast-right', right + 'px');
        this.toast.style.setProperty('--k-cart-toast-top', top + 'px');
      }

      showToast() {
        this.closeDrawer(false);
        this.positionToast();
        this.classList.add('is-toast-open');
        window.clearTimeout(this.toastTimer);
        this.toastTimer = window.setTimeout(() => {
          if (this.toast && (this.toast.matches(':hover') || this.toast.contains(document.activeElement))) {
            this.toastTimer = window.setTimeout(() => this.hideToast(), 2000);
            return;
          }
          this.hideToast();
        }, 5000);
      }

      hideToast() {
        this.classList.remove('is-toast-open');
        window.clearTimeout(this.toastTimer);
      }

      openDrawer(triggeredBy) {
        this.hideToast();
        if (triggeredBy) this.activeElement = triggeredBy;
        this.classList.add('is-open');
        this.loadDeferredImages();
        if (this.drawer) this.drawer.setAttribute('aria-hidden', 'false');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
        document.body.classList.add('k-cart-lock');

        requestAnimationFrame(() => {
          const close = this.querySelector('.k-cart__drawer-close');
          if (close) close.focus({ preventScroll: true });
        });
      }

      closeDrawer(restoreFocus = true) {
        this.classList.remove('is-open');
        if (this.drawer) this.drawer.setAttribute('aria-hidden', 'true');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('k-cart-lock');
        if (restoreFocus && this.activeElement && typeof this.activeElement.focus === 'function') {
          this.activeElement.focus({ preventScroll: true });
        }
      }

      async changeLine(line, quantity) {
        this.classList.add('is-updating');
        const status = this.querySelector('[data-k-cart-status]');
        if (status) status.textContent = 'Mise à jour du panier…';

        try {
          const response = await fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              line,
              quantity,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });

          if (!response.ok) throw new Error('Cart update failed: ' + response.status);
          const state = await response.json();

          if (state.sections && state.sections['cart-drawer']) this.replaceDrawer(state.sections['cart-drawer']);
          if (state.sections && state.sections['cart-icon-bubble']) this.replaceIcon(state.sections['cart-icon-bubble']);
          this.syncTriggerLabel();

          const nextStatus = this.querySelector('[data-k-cart-status]');
          if (nextStatus) nextStatus.textContent = 'Panier mis à jour.';

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'k-cart',
              cartData: state,
            });
          }
        } catch (error) {
          console.error(error);
          const currentStatus = this.querySelector('[data-k-cart-status]');
          if (currentStatus) currentStatus.textContent = 'Impossible de mettre à jour le panier. Réessayez.';
        } finally {
          this.classList.remove('is-updating');
        }
      }
    }
  );
}
